/**
 * Obsidian-style `[[wikilink]]` expansion.
 *
 * Wikilinks are pure authoring sugar — they get expanded into standard
 * markdown links (`[label](path.md)`) during weave so the exported `.md`
 * files contain plain portable markdown.
 *
 * Supported syntax:
 *   [[name]]                  -> [name](path-to-name.md)
 *   [[name|Custom Label]]     -> [Custom Label](path-to-name.md)
 *   [[folder/name]]           -> [folder/name](relative-path-to-folder/name.md)
 *   [[folder/name|Label]]     -> [Label](relative-path-to-folder/name.md)
 *   [[name#section]]          -> [name](path.md#section)
 *
 * Fenced code blocks and inline backtick spans are excluded from expansion.
 */

import { resolveLink, relativeBetween, splitAnchor } from './link-resolver.js';

const WIKILINK_REGEX = /\[\[([^\n]+?)\]\]/g;

export interface WikilinkExpandOptions {
  /** Path of the file being woven (e.g. `setup/install.src.md`) — used for relative-path computation. */
  sourcePath?: string;
  /** All page paths in the project. If omitted, links are expanded literally without resolution. */
  allPaths?: readonly string[];
}

/**
 * Expand wikilinks in content. Returns content with `[[X]]` replaced by `[X](X.md)`.
 *
 * - If `allPaths` is provided, resolved links are emitted as relative paths
 *   from the source file (so they survive markdown export).
 * - If a target doesn't resolve to a known page, the link is still emitted
 *   so the LinkNavigationEnricher can surface it as a broken link in the GUI.
 */
export function expandWikilinks(
  content: string,
  options: WikilinkExpandOptions = {}
): string {
  return processOutsideCode(content, (chunk) =>
    chunk.replace(WIKILINK_REGEX, (_match, inner: string) =>
      expandSingleWikilink(inner, options)
    )
  );
}

function expandSingleWikilink(inner: string, options: WikilinkExpandOptions): string {
  const { sourcePath, allPaths } = options;

  const pipeIdx = inner.indexOf('|');
  const rawTarget = (pipeIdx === -1 ? inner : inner.slice(0, pipeIdx)).trim();
  const label = pipeIdx === -1 ? '' : inner.slice(pipeIdx + 1).trim();

  if (!rawTarget) {
    return `[[${inner}]]`; // Leave malformed as-is
  }

  const { base: targetBase, anchor } = splitAnchor(rawTarget);
  const displayLabel = label || targetBase;
  const anchorSuffix = anchor ? `#${anchor}` : '';

  // Try to resolve to a real page so we can emit a relative path.
  if (allPaths && allPaths.length > 0) {
    const resolved = resolveLink(rawTarget, sourcePath ?? null, allPaths);
    if (resolved && sourcePath) {
      const exportPath = resolved.path.endsWith('.src.md')
        ? resolved.path.slice(0, -'.src.md'.length) + '.md'
        : resolved.path;
      const rel = relativeBetween(sourcePath, exportPath);
      return `[${escapeMarkdown(displayLabel)}](${rel}${anchorSuffix})`;
    }
  }

  // Unresolved (or no project context): emit literal target + .md so the
  // enricher can flag it as broken in preview, and so exports still produce
  // a link that any markdown viewer can render.
  const literalTarget = targetBase.endsWith('.md') || targetBase.endsWith('.src.md')
    ? targetBase
    : `${targetBase}.md`;
  return `[${escapeMarkdown(displayLabel)}](${literalTarget}${anchorSuffix})`;
}

/** Escape `[` and `]` so they don't break the surrounding markdown link syntax. */
function escapeMarkdown(text: string): string {
  return text.replace(/[\[\]]/g, '\\$&');
}

/**
 * Apply `transform` to every prose region in `content`, leaving fenced code
 * blocks (```...```) and inline backtick spans untouched. Concatenation of
 * the returned chunks exactly reproduces the original content.
 */
function processOutsideCode(content: string, transform: (chunk: string) => string): string {
  const lines = content.split('\n');
  const out: string[] = [];
  let inFence = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isFenceLine = /^\s*```/.test(line);

    if (isFenceLine) {
      out.push(line);
      inFence = !inFence;
    } else if (inFence) {
      out.push(line);
    } else {
      out.push(transformLineSkippingInlineCode(line, transform));
    }

    if (i < lines.length - 1) out.push('\n');
  }

  return out.join('');
}

/** Apply transform to prose portions of a single line, preserving `inline code` spans. */
function transformLineSkippingInlineCode(line: string, transform: (chunk: string) => string): string {
  const parts: string[] = [];
  let i = 0;
  while (i < line.length) {
    const tickIdx = line.indexOf('`', i);
    if (tickIdx === -1) {
      parts.push(transform(line.slice(i)));
      break;
    }
    if (tickIdx > i) parts.push(transform(line.slice(i, tickIdx)));
    const endIdx = line.indexOf('`', tickIdx + 1);
    if (endIdx === -1) {
      // Unclosed backtick — treat the rest as prose
      parts.push(transform(line.slice(tickIdx)));
      break;
    }
    parts.push(line.slice(tickIdx, endIdx + 1));
    i = endIdx + 1;
  }
  return parts.join('');
}
